// Phase E1's photo mode (decision 0060: pause, a free camera, the HUD hidden, depth of field, time of day, a hero
// pose), opened from the pause screen, so the sim is already paused under it (the runtime keeps drawing frames for it).
//   - the camera: @rpg/meta's `PhotoMode` keeps a pivot the player moves within a radius of the hero; the follow
//     camera's rig follows it as its target, under the photo profile on the profile stack (the camera-profiles table's
//     `photo`, on its own layer), its arm still colliding; on exit the rig gets back its own snapshot (yaw, pitch,
//     requests) and its target: photo mode releases only what it asked for;
//   - input: @rpg/ui's `PhotoSurface` (keys, mouse, touch, pad) under a docked panel of controls;
//   - the HUD and world-space UI hide under the screen (the HUD's rule: no screen over play), and so does everything
//     else drawn over the game (notices, captions, prompts) while photo mode is up;
//   - depth of field: @rpg/look's bokeh pass in the look's post chain, focused on the hero, on the high tier; the low
//     tier has no post chain, so none (the panel says so: agent call, recorded);
//   - time of day: the sky shows the chosen hour (src/world/index.ts `setViewHours`), the sim's clock untouched, put
//     back on exit;
//   - a pose: the Wanderer's figure held in a pose from the poses table (@rpg/figure's pose layer, `actionPose`), its
//     joints put back on exit;
//   - capture: the frame drawn and saved as a PNG download.
// Planted defect `photo-unbounded` (@rpg/meta's META_FAULTS.photoUnbounded): the pivot leaves the radius.
import * as THREE from "three";
import { h, Button, Choice, Screen, Slider, Status, Toggle, PhotoSurface, type ScreenContext, type ScreenRender, type Ui } from "@rpg/ui";
import { createDepthOfField, type DepthOfField } from "@rpg/look";
import { PhotoMode } from "@rpg/meta";
import { createPoseLayer, createPoseLibrary, resetTurns, type ActionPose, type PoseLayer } from "@rpg/figure";
import type { CameraTarget, RigSnapshot } from "@rpg/camera";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { PhotoHooks } from "../sim";
import type { MetaContent } from "./content";

/** What photo mode needs from the page. */
export interface PhotoDeps {
  runtime: Runtime;
  content: SpikeContent;
  meta: MetaContent;
  hooks: PhotoHooks;
  ui: Ui;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  /** The `photo.speed` setting. */
  speed(): number;
  /** The look's tier ("low" | "high"). */
  tier: string;
  /** Tell the dev harness's compile watchdog that the next frame's compiles are deliberate (photo mode opening, its
   *  depth of field turned on: the watchdog's "deliberate work, a capture"); nothing in a production build. */
  excuse?: (reason: string) => void;
  /** Something else holds the camera's overlay layer (the dev editor's free camera): photo mode doesn't open, and its
   *  screen, if pushed, goes again. */
  blocked?: () => boolean;
}

/** What photo mode reports (for the handle and the specs). */
export interface PhotoReport {
  active: boolean;
  pivot: { x: number; y: number; z: number };
  anchor: { x: number; y: number; z: number };
  offset: number;
  radius: number;
  yaw: number;
  pitch: number;
  fov: number;
  hours: number | null;
  simHours: number;
  shownHours: number;
  pose: string | null;
  dof: { available: boolean; enabled: boolean; focus: number; aperture: number };
  profile: string;
  panelHidden: boolean;
  captures: number;
  lastCapture: { name: string; bytes: number; width: number; height: number } | null;
  /** What was hidden over the game while photo mode is up. */
  hidden: number;
}

/** The hero's pivot height over its feet (the explore profile's). */
const PIVOT_HEIGHT = 1.45;

/** Mount photo mode: its screen joins the UI; `open()` from the pause screen. */
export function mountPhoto(d: PhotoDeps) {
  const t = d.content.t;
  const cfg = d.meta.photo;
  const photo = new PhotoMode(cfg.options, (x, z) => d.hooks.ground(x, z));
  const cam = d.hooks.camera;
  const rig = cam.rig;
  const library = createPoseLibrary(d.content.registry.rows("poses") as unknown as ActionPose[]);
  let dof: DepthOfField | null = null;
  let layer: PoseLayer | null = null;
  let saved: { rig: RigSnapshot; joints: Map<string, THREE.Quaternion>; pivot: { p: THREE.Vector3; q: THREE.Quaternion } | null } | null = null;
  let hidden: { el: HTMLElement; was: string }[] = [];
  let captures = 0;
  let lastCapture: PhotoReport["lastCapture"] = null;
  let status = "";

  // the depth of field, made and drawn once now, before the page is ready: its bokeh shader and the depth programs it
  // draws the scene with compile at boot, not as a hitch when photo mode first opens (the compile watchdog's rule)
  {
    const look = d.hooks.look();
    dof = createDepthOfField({ chain: look?.composer ?? null, scene: d.scene, camera: d.camera, aperture: photo.state().dof.aperture, maxBlur: cfg.maxBlur });
    if (look && dof.available) {
      dof.enabled = true;
      look.render();
      dof.enabled = false;
    }
  }

  // the photo target: the pivot photo mode keeps, still (the profile's pivot height is 0)
  const target: CameraTarget = {
    sample: () => {
      const p = photo.state().pivot;
      return { position: { ...p }, velocity: { x: 0, y: 0, z: 0 }, facing: null, grounded: true };
    },
  };

  const figure = () => d.hooks.figure();
  const applyPose = (id: string | null): void => {
    const fig = figure();
    if (!fig || !saved) return;
    // back to the pose photo mode found, then the chosen one held on top
    for (const [jid, q] of saved.joints) fig.joints.get(jid)?.quaternion.copy(q);
    if (saved.pivot) {
      fig.pivot.position.copy(saved.pivot.p);
      fig.pivot.quaternion.copy(saved.pivot.q);
    }
    const row = id ? cfg.poses.find((p) => p.id === id) : undefined;
    if (row) {
      layer ??= createPoseLayer(fig, library);
      resetTurns(fig);
      layer.actionPose(row.pose, row.at);
    }
    fig.root.updateMatrixWorld(true);
    photo.setPose(row ? row.id : null);
  };

  const hideOverlays = (): void => {
    hidden = [];
    for (const el of Array.from(document.body.children) as HTMLElement[]) {
      if (el === d.renderer.domElement || el === d.ui.root || el.tagName === "SCRIPT" || el.tagName === "STYLE") continue;
      hidden.push({ el, was: el.style.visibility });
      el.style.visibility = "hidden";
    }
  };
  const showOverlays = (): void => {
    for (const x of hidden) x.el.style.visibility = x.was;
    hidden = [];
  };

  /** Photo mode's screen came up (the pause screen's button pushes it): set the camera, the look and the overlays. */
  const begin = (): void => {
    const fig = figure();
    const hero = d.hooks.hero();
    saved = {
      rig: rig.snapshot(),
      joints: new Map(fig ? [...fig.joints].map(([id, j]) => [id, j.quaternion.clone()]) : []),
      pivot: fig ? { p: fig.pivot.position.clone(), q: fig.pivot.quaternion.clone() } : null,
    };
    photo.enter({ x: hero.x, y: hero.y + PIVOT_HEIGHT, z: hero.z }, { yaw: rig.yaw, pitch: rig.pitch, fov: d.camera.fov, hours: d.hooks.shownHours() });
    rig.setTarget(target);
    cam.request(cfg.profile, { blend: 0 });
    rig.snap();
    status = "";
    hideOverlays();
    d.excuse?.("photo mode opened");
  };
  /** Open photo mode from the pause screen (as its button does); false anywhere else. */
  const open = (): boolean => {
    if (photo.active || d.ui.top() !== "pause" || d.blocked?.()) return false;
    d.ui.push("photo");
    return true;
  };

  const close = (): void => {
    if (!photo.active) return;
    photo.exit();
    // the rig as photo mode found it: its own yaw, pitch and profile requests, and its own target
    if (saved) {
      applyPose(null);
      rig.restore(saved.rig);
    }
    rig.setTarget(cam.target);
    rig.snap();
    d.hooks.setViewHours(null);
    if (dof) dof.enabled = false;
    saved = null;
    showOverlays();
  };
  // photo mode follows its screen: up (the pause screen's button), and gone however it goes (back, a resume)
  d.ui.stack.onChange(() => {
    const up = d.ui.ids().includes("photo");
    if (up && !photo.active) {
      // the pause screen's button pushed it while the editor holds the overlay layer: back to the pause screen
      if (d.blocked?.()) queueMicrotask(() => void (d.ui.top() === "photo" && d.ui.pop()));
      else begin();
    } else if (!up && photo.active) close();
  });

  // each drawn frame, after the rig placed the camera: the lens, the depth of field's focus on the hero
  const heroAt = new THREE.Vector3();
  d.runtime.on(
    "late",
    () => {
      if (!photo.active) return;
      const s = photo.state();
      photo.sync(rig.yaw, rig.pitch);
      // the pose held over whatever posed the figure this frame (a deterministic advance runs the hero's motion)
      if (s.pose) applyPose(s.pose);
      if (Math.abs(d.camera.fov - s.fov) > 1e-6) {
        d.camera.fov = s.fov;
        d.camera.updateProjectionMatrix();
      }
      if (dof?.available) {
        const hero = d.hooks.hero();
        heroAt.set(hero.x, hero.y + 1, hero.z);
        dof.set({ focus: d.camera.position.distanceTo(heroAt), aperture: s.dof.aperture });
        // the depth pass draws the scene with its own programs: a piece first seen through it compiles one (warmed at
        // boot for what was loaded then)
        if (s.dof.on && !dof.enabled) d.excuse?.("photo mode: depth of field on");
        dof.enabled = s.dof.on;
      }
    },
    { order: -5, name: "meta: photo lens" },
  );

  const capture = async (): Promise<void> => {
    const look = d.hooks.look();
    if (look) look.render();
    else d.renderer.render(d.scene, d.camera);
    const canvas = d.renderer.domElement;
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/png"));
    if (!blob) return;
    const now = new Date();
    const pad = (n: number): string => String(n).padStart(2, "0");
    const name = `${cfg.file}-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.png`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    captures++;
    lastCapture = { name, bytes: blob.size, width: canvas.width, height: canvas.height };
    status = t("photo.saved");
    d.ui.refresh();
  };

  const hourText = (h: number): string => {
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    return t("photo.time.value", { hours: String(hh % 24).padStart(2, "0"), minutes: String(mm % 60).padStart(2, "0") });
  };

  const screen: ScreenRender = (ctx: ScreenContext) => {
    const s = photo.state();
    const surface = h(PhotoSurface, {
      label: t("photo.surface"),
      // the runtime's frames (the page's own, or a test's deterministic advance)
      frames: (tick: (dt: number) => void) => d.runtime.on("late", (c) => tick(c.realDt), { name: "meta: photo input" }),
      handlers: {
        move: (f, r, u, dt) => {
          const k = d.speed();
          photo.move(f * k, r * k, u * k, dt);
        },
        orbit: (dy, dp) => {
          const a = photo.orbit(dy, dp);
          rig.look(a.dYaw, a.dPitch);
          photo.sync(rig.yaw, rig.pitch);
        },
        zoom: (f) => {
          photo.zoom(f);
          ctx.ui.refresh();
        },
      },
    });
    if (s.panelHidden)
      return h(
        "div",
        null,
        surface,
        h(Screen, { ctx, title: t("photo.title"), variant: "dock" }, h(Button, { label: t("photo.show"), action: "photo-show", autofocus: true, onPress: () => (photo.setPanelHidden(false), ctx.ui.refresh()) })),
      );
    const shownHours = s.hours ?? d.hooks.simHours();
    const options = [{ value: "none", label: t("photo.pose.none") }, ...cfg.poses.map((p) => ({ value: p.id, label: t(p.label) }))];
    return h(
      "div",
      null,
      surface,
      h(
        Screen,
        { ctx, title: t("photo.title"), variant: "dock" },
        h(Slider, {
          id: "photo-time",
          label: t("photo.time"),
          value: shownHours,
          min: 0,
          max: 23.75,
          step: 0.25,
          valueText: hourText(shownHours),
          onChange: (v) => {
            photo.setHours(v);
            d.hooks.setViewHours(photo.state().hours);
            ctx.ui.refresh();
          },
        }),
        h(Slider, {
          id: "photo-zoom",
          label: t("photo.zoom"),
          value: cfg.options.fov[1] + cfg.options.fov[0] - s.fov,
          min: cfg.options.fov[0],
          max: cfg.options.fov[1],
          step: 1,
          valueText: t("photo.zoom.value", { fov: Math.round(s.fov) }),
          onChange: (v) => {
            photo.zoom(s.fov / (cfg.options.fov[1] + cfg.options.fov[0] - v));
            ctx.ui.refresh();
          },
        }),
        dof?.available
          ? h(Toggle, { id: "photo-dof", label: t("photo.dof"), on: s.dof.on, onText: t("option.on"), offText: t("option.off"), onChange: (on) => (photo.setDof({ on }), ctx.ui.refresh()) })
          : h(Status, { text: t("photo.dof.none") }),
        dof?.available && s.dof.on
          ? h(Slider, {
              id: "photo-blur",
              label: t("photo.blur"),
              value: s.dof.aperture,
              min: cfg.options.aperture[0],
              max: cfg.options.aperture[1],
              step: (cfg.options.aperture[1] - cfg.options.aperture[0]) / 20,
              valueText: t("photo.blur.value", { percent: Math.round(((s.dof.aperture - cfg.options.aperture[0]) / (cfg.options.aperture[1] - cfg.options.aperture[0])) * 100) }),
              onChange: (v) => (photo.setDof({ aperture: v }), ctx.ui.refresh()),
            })
          : null,
        h(Choice, { id: "photo-pose", label: t("photo.pose"), value: s.pose ?? "none", options, onChange: (v) => (applyPose(v === "none" ? null : v), ctx.ui.refresh()) }),
        h(Button, { label: t("photo.capture"), variant: "primary", action: "photo-capture", autofocus: true, onPress: () => void capture() }),
        h(Button, { label: t("photo.reset"), action: "photo-reset", onPress: () => (photo.place(photo.state().anchor), ctx.ui.refresh()) }),
        h(Button, { label: t("photo.hide"), action: "photo-hide", onPress: () => (photo.setPanelHidden(true), ctx.ui.refresh()) }),
        status ? h(Status, { text: status }) : null,
      ),
    );
  };

  const report = (): PhotoReport => {
    const s = photo.state();
    return {
      active: s.active,
      pivot: s.pivot,
      anchor: s.anchor,
      offset: photo.offset(),
      radius: cfg.options.radius,
      yaw: s.yaw,
      pitch: s.pitch,
      fov: s.fov,
      hours: s.hours,
      simHours: d.hooks.simHours(),
      shownHours: d.hooks.shownHours(),
      pose: s.pose,
      dof: dof ? { available: dof.available, enabled: dof.enabled, focus: dof.state().focus, aperture: dof.state().aperture } : { available: d.tier !== "low", enabled: false, focus: 0, aperture: 0 },
      profile: rig.profiles.state().active,
      panelHidden: s.panelHidden,
      captures,
      lastCapture,
      hidden: hidden.length,
    };
  };

  return {
    screens: { photo: screen } as Record<string, ScreenRender>,
    pause: [{ label: "pause.photo", action: "photo", screen: "photo" }],
    open,
    close,
    active: (): boolean => photo.active,
    report,
    /** For the specs: drive the free camera as the surface does. */
    move: (f: number, r: number, u: number, dt: number) => photo.move(f, r, u, dt),
    orbit: (dy: number, dp: number) => {
      const a = photo.orbit(dy, dp);
      rig.look(a.dYaw, a.dPitch);
      photo.sync(rig.yaw, rig.pitch);
    },
    zoom: (f: number) => photo.zoom(f),
    setHours: (hrs: number | null) => {
      photo.setHours(hrs);
      d.hooks.setViewHours(photo.state().hours);
      d.ui.refresh();
    },
    setPose: (id: string | null) => {
      applyPose(id);
      d.ui.refresh();
    },
    setDof: (o: { on?: boolean; aperture?: number }) => {
      photo.setDof(o);
      d.ui.refresh();
    },
    hidePanel: (on: boolean) => {
      photo.setPanelHidden(on);
      d.ui.refresh();
    },
    capture,
    /** A joint of the Wanderer's figure: its turn now (x, y, z, w), for the pose checks. */
    joint: (id: string): [number, number, number, number] | null => {
      const q = figure()?.joints.get(id)?.quaternion;
      return q ? [q.x, q.y, q.z, q.w] : null;
    },
  };
}

/** Mounted photo mode. */
export type PhotoView = ReturnType<typeof mountPhoto>;
